import { Pergunta } from "../../services/pesquisa";
import { Radio, RadioGroup } from "../Radio/Radio";

export type RespostaValor = number | string | boolean | null;

export type Respostas = Record<string, RespostaValor>;

type SurveyRendererProps = {
  perguntas: Pergunta[];
  respostas: Respostas;
  onChange: (chave: string, valor: RespostaValor) => void;
};

const NOTAS_1_5 = [1, 2, 3, 4, 5];
const NOTAS_0_10 = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const comoTexto = (valor: RespostaValor): string | null => {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === "boolean") return valor ? "Sim" : "Não";
  return String(valor);
};

export const perguntaVisivel = (pergunta: Pergunta, respostas: Respostas): boolean => {
  if (!pergunta.exibirSe) return true;

  const origem = comoTexto(respostas[pergunta.exibirSe.chave]);
  return origem !== null && origem.toLowerCase() === pergunta.exibirSe.valor.toLowerCase();
};

export const SurveyRenderer = ({ perguntas, respostas, onChange }: SurveyRendererProps) => {
  return (
    <div className="flex flex-col gap-6 font-kumbh-sans">
      {perguntas.filter((pergunta) => perguntaVisivel(pergunta, respostas)).map((pergunta) => (
        <fieldset key={pergunta.chave} className="flex flex-col gap-3">
          <legend className="text-sm font-semibold text-zinc-800 mb-2">
            {pergunta.texto}
            {!pergunta.obrigatoria && <span className="ml-1 font-normal text-zinc-500">(opcional)</span>}
          </legend>

          <PerguntaCampo pergunta={pergunta} valor={respostas[pergunta.chave] ?? null} onChange={onChange} />
        </fieldset>
      ))}
    </div>
  );
};

type PerguntaCampoProps = {
  pergunta: Pergunta;
  valor: RespostaValor;
  onChange: (chave: string, valor: RespostaValor) => void;
};

const PerguntaCampo = ({ pergunta, valor, onChange }: PerguntaCampoProps) => {
  switch (pergunta.tipo) {
    case "SIM_NAO":
      return (
        <RadioGroup
          orientation="horizontal"
          value={valor === null ? "" : String(valor)}
          onValueChange={(v) => onChange(pergunta.chave, v === "true")}
          aria-label={pergunta.texto}
        >
          {[
            { value: "true", label: "Sim" },
            { value: "false", label: "Não" },
          ].map((opcao) => (
            <Radio
              key={opcao.value}
              value={opcao.value}
              selectedValue={valor === null ? "" : String(valor)}
              isSelected={String(valor) === opcao.value}
            >
              {opcao.label}
            </Radio>
          ))}
        </RadioGroup>
      );

    case "ESCALA_1_5":
    case "ESCALA_0_10":
      return <EscalaDeNotas pergunta={pergunta} valor={valor} onChange={onChange} />;

    case "MULTIPLA_ESCOLHA":
      return (
        <RadioGroup
          value={typeof valor === "string" ? valor : ""}
          onValueChange={(v) => onChange(pergunta.chave, v)}
          aria-label={pergunta.texto}
        >
          {(pergunta.opcoes ?? []).map((opcao) => (
            <Radio key={opcao} value={opcao} selectedValue={valor} isSelected={valor === opcao}>
              {opcao}
            </Radio>
          ))}
        </RadioGroup>
      );

    case "TEXTO_LIVRE":
    default:
      return (
        <textarea
          value={typeof valor === "string" ? valor : ""}
          onChange={(e) => onChange(pergunta.chave, e.target.value)}
          maxLength={2000}
          rows={3}
          aria-label={pergunta.texto}
          className="w-full rounded-xl border border-zinc-200 p-3 text-sm text-zinc-800 resize-none
                     focus:outline-none focus:border-[var(--main-color)]"
          placeholder={pergunta.obrigatoria ? "Escreva aqui" : "Escreva aqui (opcional)"}
        />
      );
  }
};

const EscalaDeNotas = ({ pergunta, valor, onChange }: PerguntaCampoProps) => {
  const ehNps = pergunta.tipo === "ESCALA_0_10";
  const notas = ehNps ? NOTAS_0_10 : NOTAS_1_5;

  return (
    <div>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={pergunta.texto}>
        {notas.map((nota) => {
          const selecionada = valor === nota;
          return (
            <button
              key={nota}
              type="button"
              role="radio"
              aria-checked={selecionada}
              aria-label={`Nota ${nota} de ${ehNps ? 10 : 5}`}
              onClick={() => onChange(pergunta.chave, nota)}
              className={[
                ehNps ? "w-9 h-9 text-sm" : "w-11 h-11 text-base",
                "rounded-xl font-semibold transition-all duration-200",
                "shadow-[0px_4px_4px_0.5px_rgba(0,0,0,0.05)]",
                selecionada
                  ? "bg-[var(--main-color)] text-[var(--continue-button-font)] shadow-md"
                  : "bg-white text-zinc-700 opacity-90 hover:opacity-100",
              ].join(" ")}
            >
              {nota}
            </button>
          );
        })}
      </div>

      {ehNps && (
        <div className="flex justify-between text-xs text-zinc-500 mt-2">
          <span>Não recomendaria</span>
          <span>Recomendaria muito</span>
        </div>
      )}
    </div>
  );
};
